/**
 * SurfaceIndex.tsx — every surface this workbench offers, and what it can serve.
 *
 * The index is where the honest tally lives. Each surface is listed with the
 * number of slots the Engine's manifest actually serves and the number it does
 * not, so a user can tell at a glance which discipline views are complete against
 * THIS Engine and which have gaps — before opening one and discovering it.
 *
 * The tally is computed from the same `resolveSurface` the surface page uses, so
 * the index cannot disagree with the page it links to. A second calculation here
 * would be a second place for availability to be decided, which is precisely what
 * the slot table refuses to be.
 */

import { useMemo } from "react";
import { useClient } from "../../api/ClientContext";
import { CapabilityManifestError } from "../../api/capabilityManifest";
import { routeHref } from "../../app/routing";
import { ErrorBanner } from "../../components/ErrorBanner";
import { Empty, Panel } from "../../components/Panel";
import { useAsync } from "../../stores/useAsync";
import { ManifestRefusal } from "../capabilities/ManifestRefusal";
import { resolveAllSurfaces } from "./surfaceSlots";

export function SurfaceIndex({ projectId }: { projectId: string }): JSX.Element {
  const client = useClient();
  const published = useAsync(() => client.capabilities(), [client]);
  const surfaces = useMemo(
    () => (published.data ? resolveAllSurfaces(published.data) : []),
    [published.data],
  );

  return (
    <div className="page">
      <header className="page-head">
        <h1>Disciplines</h1>
        <p className="muted">
          One shared workbench. Each surface is a list of what an engineer needs, resolved against the Engine&apos;s
          own manifest; the interaction behind every one of them is the same generic loop.
        </p>
      </header>

      {published.error instanceof CapabilityManifestError && <ManifestRefusal error={published.error} />}
      <ErrorBanner
        error={published.error && !(published.error instanceof CapabilityManifestError) ? published.error : undefined}
        onDismiss={published.reload}
      />

      {surfaces.length === 0
        ? <Panel title="Surfaces"><Empty>Reading the Engine&apos;s published manifest…</Empty></Panel>
        : (
          <Panel title="Surfaces">
            <ul className="surface-index">
              {surfaces.map(surface => (
                <li key={surface.definition.key} data-surface={surface.definition.key}>
                  <a href={routeHref({ name: "surface", projectId, surface: surface.definition.key })}>
                    <span className="capability-id">{surface.definition.title}</span>
                    <span className="muted">{surface.definition.purpose}</span>
                    <span
                      className={surface.absent.length === 0 ? "good" : "bad"}
                      data-stage="surface-tally-cell"
                      data-served={String(surface.served.length)}
                      data-absent={String(surface.absent.length)}
                    >
                      {surface.served.length} served · {surface.absent.length} not published
                    </span>
                  </a>
                </li>
              ))}
            </ul>
            <p className="muted">
              &ldquo;Not published&rdquo; is a fact about the Engine&apos;s manifest, not about the project. The
              capability is absent from the contract this workbench was given, so the surface names it rather than
              showing something in its place.
            </p>
          </Panel>
        )}
    </div>
  );
}
